import { useMemo, type ReactNode } from 'react';
import clsx from 'clsx';
import type { ProjectGraphEdge, ProjectGraphNode, ProjectGraphResponse } from '@avisoma/shared';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { edgesWithoutPages, unconnectedDocuments, type DocumentNode } from './graphAudit';
import { nodeLabel } from './graphModel';
import { NodeButton } from './NodeButton';
import { VIRTUAL_MIN, VirtualList } from './VirtualList';

type Props = {
  graph: ProjectGraphResponse;
  nodesById: ReadonlyMap<string, ProjectGraphNode>;
  /** Shows the document in the view, selects it, and pans to it. */
  onSelect: (id: string) => void;
};

/** A row's fixed height in a windowed list, spacing included (`h-7`). */
const ROW_HEIGHT = 28;
const documentKey = (node: DocumentNode) => node.id;
const edgeKey = (edge: ProjectGraphEdge) => edge.id;

function Section<T>({
  title,
  items,
  getKey,
  renderRow,
}: {
  title: string;
  items: readonly T[];
  getKey: (item: T) => string;
  renderRow: (item: T, truncate: boolean) => ReactNode;
}) {
  // A windowed list needs rows of one height, so long labels are cut to one line there.
  const windowed = items.length > VIRTUAL_MIN;
  return (
    <div className="flex flex-col gap-1">
      <div className="font-medium">
        {title} ({items.length.toLocaleString('en-US')})
      </div>
      {items.length === 0 ? (
        <p className="text-zinc-500 dark:text-zinc-400">None.</p>
      ) : (
        <VirtualList
          items={items}
          getKey={getKey}
          rowHeight={ROW_HEIGHT}
          spacing={1}
          rowClassName={clsx(windowed && 'flex h-7 items-center')}
          renderRow={(item) => renderRow(item, windowed)}
        />
      )}
    </div>
  );
}

/**
 * Loose ends of the graph: documents that name no entity, and mentions that no linked page backs. Shown only
 * when either list has a row; a click selects the document.
 */
export function GraphAudit({ graph, nodesById, onSelect }: Props) {
  const unconnected = useMemo(() => unconnectedDocuments(graph), [graph]);
  const withoutPages = useMemo(() => edgesWithoutPages(graph), [graph]);
  if (unconnected.length === 0 && withoutPages.length === 0) return null;
  const labelOf = (id: string) => {
    const node = nodesById.get(id);
    return node ? nodeLabel(node) : id;
  };

  return (
    <CollapsibleCard storageKey="projectGraph.audit" title="Audit" summary={unconnected.length + withoutPages.length}>
      <div className="flex flex-col gap-3 py-1 text-sm text-zinc-900 dark:text-zinc-100">
        <Section
          title="Documents with no connections"
          items={unconnected}
          getKey={documentKey}
          renderRow={(node, truncate) => (
            <NodeButton label={node.title} truncate={truncate} onClick={() => onSelect(node.id)} />
          )}
        />
        <Section
          title="Mentions without a linked page"
          items={withoutPages}
          getKey={edgeKey}
          renderRow={(edge, truncate) => (
            <NodeButton
              label={`${labelOf(edge.to)} ← ${labelOf(edge.from)}`}
              truncate={truncate}
              onClick={() => onSelect(edge.from)}
            />
          )}
        />
      </div>
    </CollapsibleCard>
  );
}
